import { createFeatureSelector, createSelector } from "@ngrx/store";
import { isDevMode } from "@angular/core";
import { WidgetState, FeatureKey } from "../reducers";
import { getRouterSelectors } from "@ngrx/router-store";
const { selectQueryParam } = getRouterSelectors();
import * as _ from 'lodash';
import { DatesAndAccupancy, HotelBookingDataDetails, NO_IMAGE } from "../index.types";
import moment from "moment";
import { BestPriceSorter, BuildSearchParams } from "app/helpers";

export const WidgetFeature = createFeatureSelector<WidgetState>(FeatureKey);

export const selectWidgetData = createSelector(WidgetFeature, (state: WidgetState) => state.widgetData);
export const selectWidgetExpanded = createSelector(WidgetFeature, (state: WidgetState) => state.expanded);
export const selectWHasComment = createSelector(WidgetFeature, (state: WidgetState) => !!state.widgetData?.detail.scwHasComment);
export const selectWHasBook = createSelector(WidgetFeature, (state: WidgetState) => !!state.widgetData?.detail.scwHasBook);
export const selectListOta = createSelector(WidgetFeature, (state: WidgetState) => state.widgetData?.detail.listOta);
export const selectClientCountry = createSelector(WidgetFeature, (state: WidgetState) => state.widgetData?.country);

export const selectTranslations = createSelector(WidgetFeature, (state: WidgetState) => ({ ...state.translations['static'], ...state.translations['staticaddvalue'] }));
export const selectRoomsNameTranslation = createSelector(WidgetFeature, (state: WidgetState) => state.translations['Chamber']);
export const selectTranslation = (props: { key: string }) => createSelector(selectTranslations, (translations) => translations[props.key]);
export const selectRoomNameTranslation = (props: { roomId: number }) => createSelector(selectRoomsNameTranslation, (translations) => translations[`${props.roomId}-titer`]);

export const selectDatesAndAccupancy = createSelector(WidgetFeature, (state: WidgetState) => state.datesAndAccupancy);
export const selectDatesAndAccupancyRange = createSelector(WidgetFeature, (state: WidgetState) => state.datesAndAccupancy?.range);
export const selectDatesAndAccupancyResult = createSelector(WidgetFeature, (state: WidgetState) => {
    const _parts = {
        from: moment(state.datesAndAccupancy?.range.from).format('DD-MM-YYYY'),
        to: moment(state.datesAndAccupancy?.range.to).format('DD-MM-YYYY'),
        adultes: state.datesAndAccupancy?.adultes,
        children: state.datesAndAccupancy?.children,
        ages: state.datesAndAccupancy?.ages.join(', '),
        rooms: state.datesAndAccupancy?.rooms
    }
    return (state.datesAndAccupancy) ? _parts : null
});

export const selectCurrentLang = selectQueryParam('lang');
export const selectComments = createSelector(WidgetFeature, selectCurrentLang, (state: WidgetState, lang) => {
    const langMapping: any = {
        'fr': 1,
        'en': 2,
        'es': 3,
        'de': 5,
        'ar': 25,
        'ru': 9
    };

    if (!state.comments) return null;

    const requestedLang = lang;
    const requestedLangId = langMapping[lang as string];

    const _filtredCommentInCurrentLang = state.comments.filter((c: any) =>
        (requestedLang && c.commentLang == requestedLang) ||
        (requestedLangId !== undefined && c.commentLang == requestedLangId)
    );

    if (_filtredCommentInCurrentLang.length > 0) {
        return _filtredCommentInCurrentLang;
    }

    // Default to English ('en' or 2)
    return state.comments.filter((c: any) => c.commentLang == 'en' || c.commentLang == 2);
});

export const selectHotelBookingData = createSelector(WidgetFeature, (state: WidgetState) => {
    if (state.hotelBookingData) {
        console.log('[selector] hotelBookingData keys:', Object.keys(state.hotelBookingData));
        // Deduplicate partner offers between 'b' and 'bc': keep only the cheaper one.
        const bPartnerTtc = state.hotelBookingData['b']?.bestPrices.partner?.ttc ?? Infinity;
        const bcPartnerTtc = state.hotelBookingData['bc']?.bestPrices.partner?.ttc ?? Infinity;
        // loser = the engine whose partner price is higher (when both exist)
        const partnerLoser = (bPartnerTtc < Infinity && bcPartnerTtc < Infinity)
            ? (bPartnerTtc <= bcPartnerTtc ? 'bc' : 'b')
            : null; // no deduplication needed if only one (or neither) has a partner

        const _hotelBookingData = Object.entries(state.hotelBookingData).map((hbd) => {
            const _clonedHbd = _.cloneDeep(hbd);
            const [engine] = hbd;
            // Null out the loser's partner offer
            if (partnerLoser && engine === partnerLoser) {
                _clonedHbd[1].bestPrices.partner = null;
            }
            return _clonedHbd;
        });
        // -------------
        const _sortedHotelBookingData = _hotelBookingData.sort(([_engA, dataA], [_engB, dataB]) => {
            const hasGoogleA = dataA.bestPrices.googleHotelAds != null;
            const hasGoogleB = dataB.bestPrices.googleHotelAds != null;
            if (hasGoogleA && !hasGoogleB) return -1;
            if (!hasGoogleA && hasGoogleB) return 1;
            return 0;
        });
        return _sortedHotelBookingData
    }
    return null
});
export const selectHotelBookingLoader = createSelector(WidgetFeature, (state: WidgetState) => state.loaders.engines);
export const selectEngine = (props: { engine: string }) => createSelector(WidgetFeature, (state: WidgetState) => state.hotelBookingData?.[props.engine]);

export const selectBestPrice = createSelector(WidgetFeature, selectHotelBookingData, selectDatesAndAccupancy, (state: WidgetState, hotelBookingData, datesAndAccupancy) => {
    const bestPricesArray = _.flatten(hotelBookingData?.map(
        ([engine, data]) => {
            const _arr = [
                data.bestPrices.bestprice,
                data.bestPrices.partner
            ];
            return _arr.filter(Boolean)
        }
    ));
    const sortedArray = bestPricesArray.sort((a, b) => BestPriceSorter(a as HotelBookingDataDetails, b as HotelBookingDataDetails, datesAndAccupancy as DatesAndAccupancy))
    return sortedArray.shift() || null
});

export const selectBestPriceEngine = createSelector(selectHotelBookingData, selectBestPrice, (hotelBookingData, bestPrice) => {
    // the purpose of this func is to map the HotelBookingData engine and ota to a keyword, this will help to reconize the source of best price by color in production
    let bestPriceEngine = hotelBookingData?.map(
        ([engine, data]) => {
            return Object.entries({
                b: data.bestPrices.bestprice,
                p: data.bestPrices.partner
            }).filter(([ota, otaData]) => otaData?.indexSearch == bestPrice?.indexSearch)?.map(([ota, otaData]) => `${engine}-${ota}`).at(0);
        }
    ).filter(Boolean).at(0)

    return bestPriceEngine
});

export const selectBestPriceMealPLan = createSelector(selectBestPrice, (bestPrice) => {
    const mealplan = bestPrice?.addedvalues.filter(a => a.category == 'mealplan');
    return (mealplan && mealplan.length > 0) ? mealplan[0] : bestPrice?.addedvalues[0]
})

export const selectMaxPrice = createSelector(selectHotelBookingData, (hotelBookingData) => {
    if (!hotelBookingData) return null;

    let maxPrice = 0;
    const normalizePrice = (value: number | null | undefined): number => {
        return Number.isFinite(value) ? Number(value) : 0;
    };

    for (const [engineKey, engineData] of hotelBookingData) {
        const bestPrices = engineData.bestPrices;

        // if (bestPrices.googleHotelAds && isDevMode()) {
        //     maxPrice = Math.max(maxPrice, normalizePrice(bestPrices.googleHotelAds.detailPrice?.Ttc));
        // }

        if (bestPrices.bestprice && ['b', 'a', 'bc'].includes(engineKey)) {
            maxPrice = Math.max(maxPrice, normalizePrice(bestPrices.bestprice.detailPrice_without_promo?.Ttc ?? bestPrices.bestprice.detailPrice?.Ttc));
        }

        if (bestPrices.partner && (isDevMode() || engineKey === 'bc')) {
            maxPrice = Math.max(maxPrice, normalizePrice(bestPrices.partner.detailPrice_without_promo?.Ttc ?? bestPrices.partner.detailPrice?.Ttc));
        }
    }

    return maxPrice > 0 ? maxPrice : null;
});

export const selectMinPrice = createSelector(selectHotelBookingData, (hotelBookingData) => {
    if (!hotelBookingData) return null;

    let minPrice = 1e9;
    const normalizePrice = (value: number | null | undefined): number => {
        return Number.isFinite(value) ? Number(value) : 1e9;
    };

    for (const [engineKey, engineData] of hotelBookingData) {
        const bestPrices = engineData.bestPrices;

        if (bestPrices.bestprice && ['b'].includes(engineKey)) {
            minPrice = Math.min(minPrice, normalizePrice(bestPrices.bestprice.detailPrice_without_promo?.Ttc ?? bestPrices.bestprice.detailPrice?.Ttc));
        }

        // if (bestPrices.partner && (isDevMode() || engineKey === 'bc')) {
        //     minPrice = Math.min(minPrice, normalizePrice(bestPrices.partner.detailPrice_without_promo?.Ttc ?? bestPrices.partner.detailPrice?.Ttc));
        // }
    }

    return minPrice < 1e9 ? minPrice : null;
});

export const selectHotelBookingNoAvailability = createSelector(WidgetFeature, (state: WidgetState) => state.noAvailability);

export const selectProposition = createSelector(WidgetFeature, (state: WidgetState) => state.proposition);
export const selectPropositionLoader = createSelector(WidgetFeature, (state: WidgetState) => state.loaders.proposition);

export const selectOrigin = selectQueryParam('origin');
export const selectIsMobile = selectQueryParam('isMobile');
export const selectBookingLink = createSelector(selectWidgetData, selectDatesAndAccupancy, selectOrigin, selectCurrentLang, (widgetData, datesAndAccupancy, origin, lang) => {
    const start = datesAndAccupancy?.range.from.toString('YMD', '-') || moment().format('YYYY-MM-DD');
    const end = datesAndAccupancy?.range.to.toString('YMD', '-') || moment().add(1, 'days').format('YYYY-MM-DD');
    const etabName = widgetData?.hotel;
    const agesenfants = (datesAndAccupancy?.ages) ? datesAndAccupancy?.ages : undefined;

    const searchParams = {
        idetab: widgetData?.id_hotel_in_bo,
        adults: datesAndAccupancy?.adultes || 2,
        enfants: datesAndAccupancy?.children || 0,
        start_date: start,
        end_date: end
    };

    const BP_searchParams = {
        adults: datesAndAccupancy?.adultes || 2,
        enfants: datesAndAccupancy?.children || 0,
        currency: datesAndAccupancy?.currency,
        start,
        end,
        idetab: widgetData?.id_hotel_in_bo,
        city: "18",
        agesenfants
    }

    return [
        `http://thebookingbutton.site/${lang}/availibility/${etabName}?${BuildSearchParams(BP_searchParams)}`,
        `${origin}/${lang}/availibility?${BuildSearchParams(searchParams)}`
    ]
});

export const selectMainImage = (props: { code_room: number }) => createSelector(WidgetFeature, (state: WidgetState) => {
    const _img = state.imagesData?.rooms.filter(r => r.id_chambre == props.code_room)?.[0]?.img[0];
    return _img ? `${state.imagesData?.baseurl}${_img}` : NO_IMAGE
});

//** https://rate.picscache.com/images/35096/upload/300X199/${_img}

export const selectSearchPanelVisibility = createSelector(WidgetFeature, (state: WidgetState) => state.searchPanelVisibility);

//Detect if many options selector
export const selectEngineOptions = createSelector(selectHotelBookingData, (hotelBookingData) => {
    const engineOptions = _.union(_.flatten(hotelBookingData?.map(
        ([engine, data]) => {
            return Object.keys(data.engine)
        }
    )));
    return engineOptions
});
